/**
 * MONSTER TOWN — SPAWNED THINGS. OWNED BY THE INTERIORS CREW.
 *
 * The `{ kind:'spawn', what }` feedback used to go nowhere. Now a cupcake
 * actually pops out of the fridge, a plate lands on the stove, a book hops off
 * the shelf. This is the difference between "a particle happened" and "my tap
 * MADE something", which is the whole promise of LAW #1 to a five-year-old.
 *
 * Cheap by construction: a fixed pool of eight, art built ONCE per kind and
 * cloned (clones share geometry and material), nothing allocated per pop.
 * Anything the pool cannot serve is dropped silently rather than growing.
 *
 * ADDING A SPAWNABLE IS A DATA DROP into `RECIPES`.
 */

import * as THREE from 'three'
import { bus } from '../core/events'
import { engine } from '../core/engine'
import { world } from '../core/scene'
import type { Vec3 } from '../core/types'
import { ball, box, boxOn, cone, cylinder, group } from '../render/kit'
import { burst } from './fx'

const POOL_SIZE = 8
const LIFE = 2.6

interface Recipe {
  readonly make: () => THREE.Object3D
  /** Particle burst fired alongside it. */
  readonly fx: string
}

/** THE SPAWN TABLE. `what` strings come from placeable interaction handlers. */
const RECIPES: Record<string, Recipe> = {
  snack: {
    fx: 'sparkle',
    make: () => {
      const g = group('snack')
      g.add(cylinder(0.13, 0.12, { color: 'cone', seg: 10 }))
      g.add(cylinder(0.15, 0.09, { color: 'iceStrawberry', y: 0.12, seg: 10 }))
      g.add(ball(0.05, { color: 'cherry', y: 0.24, seg: 6 }))
      return g
    },
  },
  dish: {
    fx: 'steam',
    make: () => {
      const g = group('dish')
      g.add(cylinder(0.2, 0.04, { color: 'plate', seg: 12 }))
      g.add(ball(0.1, { color: 'iceMint', y: 0.08, seg: 8 }))
      g.add(ball(0.08, { color: 'roofMustard', x: 0.1, y: 0.07, seg: 8 }))
      return g
    },
  },
  book: {
    fx: 'sparkle',
    make: () => {
      const g = group('book')
      g.add(boxOn(0.22, 0.06, 0.3, { color: 'wallBlue' }))
      g.add(boxOn(0.2, 0.03, 0.28, { color: 'uiPanel', y: 0.06 }))
      return g
    },
  },
  toy: {
    fx: 'confetti',
    make: () => {
      const g = group('toy')
      g.add(ball(0.13, { color: 'monsterLime', seg: 8 }))
      g.add(cone(0.09, 0.16, { color: 'flowerRed', y: 0.13, seg: 8 }))
      return g
    },
  },
  fishFood: {
    fx: 'bubbles',
    make: () => {
      const g = group('fishFood')
      for (let i = 0; i < 4; i++) {
        g.add(box(0.05, 0.05, 0.05, { color: 'roofMustard', x: (i - 1.5) * 0.06, y: 0.05 }))
      }
      return g
    },
  },
  bubble: {
    fx: 'soap',
    make: () => group('bubble').add(ball(0.16, { color: 'glass', seg: 8 })),
  },
  cake: {
    fx: 'confetti',
    make: () => {
      const g = group('cake')
      g.add(cylinder(0.16, 0.14, { color: 'iceStrawberry', seg: 12 }))
      g.add(ball(0.05, { color: 'cherry', y: 0.18, seg: 6 }))
      return g
    },
  },
}

export function spawnableNames(): string[] {
  return Object.keys(RECIPES)
}

interface Flyer {
  obj: THREE.Object3D
  vy: number
  vx: number
  vz: number
  spin: number
  life: number
  groundY: number
}

const prototypes = new Map<string, THREE.Object3D>()
const flying: Flyer[] = []
let holder: THREE.Group | null = null

function prototypeFor(what: string): THREE.Object3D | null {
  const cached = prototypes.get(what)
  if (cached !== undefined) return cached
  const recipe = RECIPES[what]
  if (recipe === undefined) return null
  const built = recipe.make()
  built.traverse((o) => {
    const m = o as THREE.Mesh
    if (m.isMesh === true) m.castShadow = false
  })
  prototypes.set(what, built)
  return built
}

function attach(): THREE.Group | null {
  const id = world.activeSceneId
  if (id === null) return null
  const node = world.get(id)
  if (node === undefined) return null
  if (holder === null) {
    holder = new THREE.Group()
    holder.name = 'spawned'
  }
  if (holder.parent !== node.actors) node.actors.add(holder)
  return holder
}

/** Pop a thing into the world at `at`. Unknown `what` still gives a sparkle. */
export function spawnThing(what: string, at: Vec3): void {
  const recipe = RECIPES[what]
  burst(recipe?.fx ?? 'sparkle', at, 7)
  const proto = prototypeFor(what)
  if (proto === null) return
  const parent = attach()
  if (parent === null) return
  if (flying.length >= POOL_SIZE) {
    const oldest = flying.shift()
    if (oldest !== undefined) oldest.obj.removeFromParent()
  }
  const obj = proto.clone(true)
  const scale = world.get(world.activeSceneId ?? '')?.grid.tileSize ?? 1
  obj.scale.setScalar(scale)
  obj.position.set(at.x, at.y + 0.25 * scale, at.z)
  parent.add(obj)
  const rng = engine.rng
  flying.push({
    obj,
    vy: 2.6,
    vx: (rng() - 0.5) * 1.1,
    vz: (rng() - 0.5) * 1.1,
    spin: (rng() - 0.5) * 5,
    life: LIFE,
    groundY: at.y,
  })
}

export function installSpawn(): void {
  bus.on('spawn:request', (e) => spawnThing(e.what, e.at))
  bus.on('scene:left', () => clearSpawned())

  engine.addSystem({
    id: 'interactions.spawn',
    order: 74,
    fixed: (dt) => {
      for (let i = flying.length - 1; i >= 0; i--) {
        const f = flying[i]
        if (f === undefined) continue
        f.life -= dt
        f.vy -= 9 * dt
        f.obj.position.x += f.vx * dt
        f.obj.position.z += f.vz * dt
        f.obj.position.y += f.vy * dt
        f.obj.rotation.y += f.spin * dt
        if (f.obj.position.y <= f.groundY) {
          f.obj.position.y = f.groundY
          // Bounce, then settle. Nothing here can fail or be missed (LAW #2).
          if (f.vy < -0.6) {
            f.vy = -f.vy * 0.42
            f.vx *= 0.5
            f.vz *= 0.5
          } else {
            f.vy = 0
            f.vx = 0
            f.vz = 0
            f.spin *= 0.9
          }
        }
        if (f.life < 0.5) {
          const s = Math.max(0.001, f.life / 0.5)
          f.obj.scale.setScalar(s * (world.get(world.activeSceneId ?? '')?.grid.tileSize ?? 1))
        }
        if (f.life <= 0) {
          f.obj.removeFromParent()
          flying.splice(i, 1)
        }
      }
    },
  })
}

export function clearSpawned(): void {
  for (const f of flying) f.obj.removeFromParent()
  flying.length = 0
}

export function spawnedCount(): number {
  return flying.length
}
